import { memo, VFC, useRef, useEffect, useState, useCallback } from 'react';
import {
  EventContentArg,
  EventDropArg,
  FormatterInput
} from '@fullcalendar/core';
import { ColSpec } from '@fullcalendar/resource-common';

import FullCalendar from '@fullcalendar/react';
import jaLocale from '@fullcalendar/core/locales/ja';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin, {
  EventResizeDoneArg
} from '@fullcalendar/interaction';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import './style.css';
import {
  TGanttData,
  TGanttVisibleRange,
  TSiteProcessListData,
  TGanttResource
} from '../../../../types/process';
import { PositionalToolTip } from '../PositionalToolTip';
import { getRokuyo } from '../../../../utils';
import { Link } from 'react-router-dom';
import { Button } from '@material-ui/core';
import {
  siteProcessGanttChartResource,
  siteProcessDeleteGanttResourceIds
} from '../../../../GlobalState';
import { useRecoilState } from 'recoil';

export type TProps = {
  constructId?: number;
  calendarRef: any;
  eventStartEditable: boolean;
  eventDurationEditable: boolean;
  displayDailyReport: boolean;
  initialHeight: number;
  initialGanttData: TGanttData;
  initialVisibleRange: TGanttVisibleRange;
  initialResourceAreaColumns: Array<ColSpec>;
  initialResourceAreaWidth: string;
  handleEditSiteProcess: (args: EventResizeDoneArg | EventDropArg) => void;
  handleResourceOrderChange: (resources: TGanttResource[]) => void;
};

export const SiteProcessGanttChart: VFC<TProps> = memo(props => {
  const {
    constructId,
    calendarRef,
    eventStartEditable,
    eventDurationEditable,
    displayDailyReport,
    initialHeight,
    initialGanttData,
    initialVisibleRange,
    initialResourceAreaColumns,
    initialResourceAreaWidth,
    handleEditSiteProcess,
    handleResourceOrderChange
  } = props;

  const fullCalendarRef = useRef<HTMLDivElement>(null);
  // リソースの状態を初期化。各リソースに順序を示すorderIdを追加
  const [resources, setResources] = useState(
    initialGanttData?.resources.map((resource, index) => ({
      ...resource,
      orderId: index
    })) || []
  );
  const [addResourceData, setAddResourceData] = useRecoilState(
    siteProcessGanttChartResource
  );
  const [deleteResourceIds] = useRecoilState(siteProcessDeleteGanttResourceIds);

  // リソースの順序を更新する関数
  const updateResourceOrder = useCallback(
    (newResources: TGanttResource[]) => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.setOption('resources', newResources);

        // リソース一覧の名前を更新
        newResources.forEach(resource => {
          const resourceApi = calendarApi.getResourceById(resource.id);
          if (resourceApi) {
            resourceApi.setProp('title', resource.title);
          }
        });
      }
    },
    [calendarRef]
  );

  // resourcesに新規追加した項目を即時反映
  useEffect(() => {
    const newResources = addResourceData.filter(
      newRes => !resources.some(existing => existing.id === newRes.id)
    );

    if (newResources.length > 0) {
      setResources(prev => [...prev, ...newResources]);
      setAddResourceData([]);
    }
  }, [addResourceData]);

  //削除された工程をresourceから排除
  useEffect(() => {
    if (deleteResourceIds.length === 0) return;
    const filteredResources = resources.filter(
      resource => !deleteResourceIds.includes(resource.id)
    );
    setResources(filteredResources);
  }, [deleteResourceIds]);

  // ドラッグ&ドロップでリソースの順序を変更
  useEffect(() => {
    // FullCalendarのDOM要素を取得
    const fullCalendarElement = fullCalendarRef.current;
    if (!fullCalendarElement) return;

    // リソースのセルを全て取得
    const resourceCells = fullCalendarElement.querySelectorAll(
      '.fc-datagrid-cell-cushion'
    );
    let draggedElement: Element | null = null;

    // 要素からリソースIDを取得する関数
    const getResourceIdFromElement = (element: Element): string | null => {
      const titleElement = element.querySelector('.MuiTypography-root');
      const title = titleElement ? titleElement.textContent : null;
      const resource = resources.find(r => r.title === title);
      return resource ? resource.id : null;
    };

    // リソースのIDをカスタム属性として追加
    resourceCells.forEach(cell => {
      const id = getResourceIdFromElement(cell);
      if (id) {
        cell.setAttribute('data-resource-id', id);
      }
    });

    // ドラッグ開始時
    const dragStartListener = (e: DragEvent) => {
      draggedElement = e.target as Element;
      const resourceId = draggedElement.getAttribute('data-resource-id');
      e.dataTransfer?.setData('text/plain', resourceId || '');
    };

    // ドラッグ中
    const dragOverListener = (e: DragEvent) => {
      e.preventDefault();
    };

    // ドロップ時
    const dropListener = (e: DragEvent) => {
      e.preventDefault();
      //   ドロップ先取得
      const targetElement = e.target as Element;

      if (draggedElement && draggedElement !== targetElement) {
        // ドラッグ元とドロップ先のリソースを特定
        const draggedResourceId = e.dataTransfer?.getData('text/plain');
        const targetResourceId = targetElement.getAttribute('data-resource-id');

        if (draggedResourceId && targetResourceId) {
          // ドラッグ元とドロップ先のインデックスを特定
          const draggedIndex = resources.findIndex(
            r => r.id === draggedResourceId
          );
          const targetIndex = resources.findIndex(
            r => r.id === targetResourceId
          );

          if (draggedIndex !== -1 && targetIndex !== -1) {
            // リソースの順序を更新
            const newResources = [...resources];
            // ドラッグされた要素を配列から削除し、その要素を取得
            const [removed] = newResources.splice(draggedIndex, 1);
            // 削除した要素を新しい位置（targetIndex）に挿入
            newResources.splice(targetIndex, 0, removed);

            //  新しい順序でorderIdを更新
            const updatedResources = newResources.map((resource, index) => ({
              ...resource,
              orderId: index
            }));

            // 新たな順序でのリソースを更新
            setResources(updatedResources);
            updateResourceOrder(updatedResources);
            handleResourceOrderChange(updatedResources);

            // DOM上のリソースセルの属性を更新
            const resourceCells = fullCalendarRef.current?.querySelectorAll(
              '.fc-datagrid-cell-cushion[data-resource-id]'
            );
            resourceCells?.forEach(cell => {
              const resourceId = cell.getAttribute('data-resource-id');
              const resource = updatedResources.find(r => r.id === resourceId);
              if (resource) {
                cell.setAttribute('data-resource-id', resource.id);
              }
            });
          }
        }
      }
    };

    // 各リソースセルにドラッグ&ドロップのイベントリスナーを追加
    resourceCells.forEach(cell => {
      cell.setAttribute('draggable', 'true');
      cell.addEventListener('dragstart', dragStartListener as EventListener);
      cell.addEventListener('dragover', dragOverListener as EventListener);
      cell.addEventListener('drop', dropListener as EventListener);
    });

    // クリーンアップ関数：イベントリスナーを削除
    return () => {
      resourceCells.forEach(cell => {
        cell.removeEventListener(
          'dragstart',
          dragStartListener as EventListener
        );
        cell.removeEventListener('dragover', dragOverListener as EventListener);
        cell.removeEventListener('drop', dropListener as EventListener);
      });
    };
  }, [resources, updateResourceOrder, handleResourceOrderChange]);

  const slotLabelFormat: Array<FormatterInput> = [
    { year: 'numeric' },
    { month: 'long' },
    { day: 'numeric' },
    { weekday: 'short' },
    { weekday: 'short' } // 六曜用
  ];
  if (displayDailyReport) {
    slotLabelFormat.push({ weekday: 'short' }); // 日報用
  }

  return (
    <div ref={fullCalendarRef}>
      <FullCalendar
        ref={calendarRef}
        // 日本語設定
        locales={[jaLocale]}
        locale='ja'
        // TODO:.envファイルにライセンスキーを設定する（https://fullcalendar.io/license）
        schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENCE_KEY}
        // ヘッダー＋フッター＋テーブルの高さ
        height={initialHeight}
        // plugin読み込み
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        // 画面表示時の初期ビュー
        initialView={'resourceTimeline'}
        views={{
          resourceTimeline: {
            type: 'resourceTimeline',
            slotDuration: { days: 1 }
          }
        }}
        // 初期表示範囲
        visibleRange={initialVisibleRange}
        // 編集権限
        eventStartEditable={eventStartEditable}
        eventDurationEditable={eventDurationEditable}
        // インジケータの表示設定
        nowIndicator={true}
        // ヘッダ部分に表示する内容の制御
        headerToolbar={{
          left: '',
          center: '',
          right: ''
        }}
        // slotのラベル設定
        slotLabelFormat={slotLabelFormat}
        // slotラベルのスタイル設定
        slotLabelClassNames={args => {
          let styles = 'slot-style';
          if (args.level === 5) {
            styles = 'daily-report';
          }
          const weekday = args.date?.getDay();
          // 土日祝日のslotラベルの場合は色を変える
          if (args.level !== 1) {
            if (weekday === 0 || weekday === 6) {
              styles += ' holiday';
            }
          }
          return styles;
        }}
        // slotラベルのテキスト設定
        slotLabelContent={args => {
          // lebel0の年表示の年は消す（例：2023年→2023にする）
          if (args.level === 0) {
            return args.text.replace('年', '');
          }

          // level2の日表示の日は消す（例：17日→17にする）
          if (args.level === 2) {
            return args.text.replace('日', '');
          }
          // level3 土曜日と日曜日はカラーを変更
          if (args.level === 3) {
            const day = args.text;
            var colorStyle = 'general-day';
            if (day == '土') {
              colorStyle = 'saturday';
            } else if (day == '日') {
              colorStyle = 'sunday';
            }
            return <div className={colorStyle}>{args.text}</div>;
          }
          // 六曜名をいれる
          if (args.level === 4) {
            const rokuyo = getRokuyo(
              args.date.getFullYear(),
              args.date.getMonth() + 1,
              args.date.getDate()
            );
            var colorStyle = 'general-day';
            if (rokuyo == '大安') {
              colorStyle = 'lucky-day';
            }

            return <div className={colorStyle}>{rokuyo}</div>;
          }
          // 日報へのリンク
          if (args.level === 5) {
            const year = args.date.getFullYear();
            const month = String(args.date.getMonth() + 1).padStart(2, '0');
            const date = String(args.date.getDate()).padStart(2, '0');
            const searchDate = `${year}-${month}-${date}`;
            return (
              <Button
                component={Link}
                to={{
                  pathname: '/daily-reports',
                  state: {
                    constructId: constructId,
                    startDate: searchDate,
                    endDate: searchDate
                  }
                }}
              >
                日報
              </Button>
            );
          }
          return args.text;
        }}
        // slotのスタイル設定
        slotLaneClassNames={args => {
          let styles = 'slot-style';
          // 土日祝日のslotセルは色を変える
          const weekday = args.date?.getDay();
          if (weekday === 0 || weekday === 6) {
            styles += ' holiday';
          }

          return styles;
        }}
        resourceOrder={(resource1: any, resource2: any) => {
          if (resource1.constructOrder < resource2.constructOrder) {
            return -1;
          } else if (resource1.constructOrder > resource2.constructOrder) {
            return 1;
          }

          if (resource1.orderId < resource2.orderId) {
            return -1;
          } else if (resource1.orderId > resource2.orderId) {
            return 1;
          }

          if (resource1.id < resource2.id) {
            return -1;
          } else if (resource1.id > resource2.id) {
            return 1;
          }

          return 0;
        }}
        // resourceの幅
        resourceAreaWidth={initialResourceAreaWidth}
        // resourceを初回に折りたたむかどうかの設定
        resourcesInitiallyExpanded={false}
        // リソースに表示するデータの制御
        resourceAreaColumns={initialResourceAreaColumns}
        // イベントの表示順
        eventOrder={(event1: any, event2: any) => {
          if (event1.priorityOrder < event2.priorityOrder) {
            return -1;
          } else if (event1.priorityOrder > event2.priorityOrder) {
            return 1;
          }
          if (event1.order < event2.order) {
            return -1;
          } else if (event1.order > event2.order) {
            return 1;
          }
          if (event1.start < event2.start) {
            return -1;
          } else if (event1.start > event2.start) {
            return 1;
          }
          if (event1.end < event2.end) {
            return -1;
          } else if (event1.end > event2.end) {
            return 1;
          }

          return 0;
        }}
        // イベントのstyle
        eventClassNames={['event-style']}
        // イベントに表示するデータの制御
        eventContent={(eventContent: EventContentArg) => {
          const site_process: TSiteProcessListData =
            eventContent.event.extendedProps.siteProcess;
          let scheduleText = site_process.start_date;
          if (site_process.end_date) {
            scheduleText += ` - ${site_process.end_date}`;
          }

          return (
            <>
              <PositionalToolTip
                title={
                  <div>
                    {eventContent.event.title}
                    <br />
                    {scheduleText}
                    {site_process.is_alerted && (
                      <>
                        <div className='event-title-alert-icon'>
                          <ReportProblemIcon />
                        </div>
                      </>
                    )}
                  </div>
                }
              >
                <div>
                  {eventContent.event.title}
                  &emsp;
                  {scheduleText}
                  {site_process.is_alerted && (
                    <>
                      <div className='event-title-alert-icon'>
                        <ReportProblemIcon />
                      </div>
                    </>
                  )}
                </div>
              </PositionalToolTip>
            </>
          );
        }}
        //resourceデータ（大項目）
        resources={resources}
        // eventデータ（小項目）
        events={initialGanttData?.events}
        // 工程のリサイズ
        eventResize={args => {
          handleEditSiteProcess(args);
        }}
        // 工程の移動
        eventDrop={args => {
          handleEditSiteProcess(args);
        }}
      />
    </div>
  );
});
